<style lang="scss" scoped>
.page-wrap {
  padding: 8px 0 80px;
  font-family: 'Segoe UI', sans-serif;
}

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;
  border-radius: 12px 12px 0 0;

  .page-title {
    font-size: 13px;
    font-weight: 700;
    color: #389ad4;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .btn-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    background: #fff;
    color: #4a5568;
    border: 1.5px solid #dde2ec;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover { border-color: #389ad4; color: #389ad4; }
  }
}

/* ── Layout ── */
.form-layout {
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 0;
  background: #fff;
}

/* ── Section card ── */
.section-card {
  border-bottom: 1px solid #f0f3fa;
  padding: 20px 24px;

  &:last-child { border-bottom: none; }

  .section-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #389ad4;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 7px;

    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: #e8edf5;
    }
  }
}

.right-col {
  border-left: 1px solid #f0f3fa;

  .section-card { border-bottom: 1px solid #f0f3fa; }
}

/* ── Form fields ── */
.field-row {
  display: grid;
  gap: 14px;
  margin-bottom: 14px;

  &.cols-2 { grid-template-columns: 1fr 1fr; }
  &.cols-3 { grid-template-columns: 1fr 1fr 1fr; }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7a8499;

    .req { color: #e53e3e; margin-left: 2px; }
  }

  input[type="text"],
  input[type="email"],
  input[type="password"],
  textarea,
  select {
    height: 38px;
    border: 1.5px solid #dde2ec;
    border-radius: 8px;
    padding: 0 12px;
    font-size: 13px;
    color: #0d1b3e;
    background: #fff;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    font-family: inherit;

    &::placeholder { color: #b0bac9; }
    &:focus {
      border-color: #389ad4;
      box-shadow: 0 0 0 3px rgba(56,154,212,0.1);
    }
    &.is-error { border-color: #e53e3e; }
  }

  textarea {
    height: auto;
    padding: 10px 12px;
    resize: vertical;
  }

  .field-error {
    font-size: 11px;
    color: #e53e3e;
    margin-top: 2px;
  }
}

/* ── Toggle switch ── */
.toggle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  .toggle-label {
    font-size: 13px;
    color: #4a5568;
    font-weight: 500;
  }
}

.switch-wrap {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;

  input { opacity: 0; width: 0; height: 0; }

  .slider {
    position: absolute;
    inset: 0;
    background: #cbd5e0;
    border-radius: 22px;
    cursor: pointer;
    transition: background 0.2s;

    &::before {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      left: 3px;
      top: 3px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s;
    }
  }

  input:checked + .slider { background: #389ad4; }
  input:checked + .slider::before { transform: translateX(16px); }
}

/* ── Upload button ── */
.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 16px;
  background: #eef2fb;
  color: #185FA5;
  border: 1.5px solid #c5d4f0;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover { background: #389ad4; color: #fff; border-color: #389ad4; }

  input[type="file"] { display: none; }
}

.canvas-preview {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
  display: inline-block;
}

/* ── Copy from row ── */
.copy-from-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  .copy-checkbox {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    color: #4a5568;
    font-weight: 500;
    cursor: pointer;

    input[type="checkbox"] {
      width: 15px;
      height: 15px;
      accent-color: #389ad4;
      cursor: pointer;
    }
  }
}

/* ── Footer ── */
.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: #fff;
  border-top: 1px solid #eef0f8;
  border-radius: 0 0 12px 12px;

  .footer-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .btn-cancel {
    height: 36px;
    padding: 0 18px;
    background: #fff;
    color: #4a5568;
    border: 1.5px solid #dde2ec;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover { border-color: #389ad4; color: #389ad4; }
  }

  .btn-save {
    height: 36px;
    padding: 0 22px;
    background: #389ad4;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: opacity 0.2s;

    &:hover { opacity: 0.9; }

    &.secondary {
      background: #fff;
      color: #389ad4;
      border: 1.5px solid #389ad4;
      &:hover { background: #eef2fb; }
    }
  }
}

/* ── Panel wrapper ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: hidden;
}
</style>

<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="page-header">
        <span class="page-title">
          <i class="fa fa-building"></i>
          {{ isEdit ? 'Edit Service Provider' : 'Create Service Provider' }}
        </span>
        <button class="btn-back" @click="goBack()">
          <i class="fa fa-arrow-left"></i> Back
        </button>
      </div>

      <!-- Two-column form layout -->
      <div class="form-layout">

        <!-- ── Left column ── -->
        <div>

          <!-- Location Information -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-map-marker"></i> Location Information</div>

            <div class="field-row cols-2">
              <div class="field-group">
                <label>Code <span class="req">*</span></label>
                <input type="text" v-model="formData.slug" :class="errors['slug'] ? 'is-error' : ''" placeholder="e.g. acme-corp" />
                <span class="field-error" v-if="errors['slug']">{{ errors['slug'][0] }}</span>
              </div>
              <div class="field-group">
                <label>Name <span class="req">*</span></label>
                <input type="text" v-model="formData.name" :class="errors['name'] ? 'is-error' : ''" placeholder="Provider name" />
                <span class="field-error" v-if="errors['name']">{{ errors['name'][0] }}</span>
              </div>
            </div>

            <div class="field-group" style="margin-bottom:14px;">
              <label>Description <span class="req">*</span></label>
              <textarea rows="2" v-model="formData.description" :class="errors['description'] ? 'is-error' : ''" placeholder="Brief description…"></textarea>
              <span class="field-error" v-if="errors['description']">{{ errors['description'][0] }}</span>
            </div>

            <div class="field-row cols-2">
              <div class="field-group">
                <label>Address Line 1 <span class="req">*</span></label>
                <input type="text" v-model="formData.address" :class="errors['address'] ? 'is-error' : ''" />
                <span class="field-error" v-if="errors['address']">{{ errors['address'][0] }}</span>
              </div>
              <div class="field-group">
                <label>Address Line 2</label>
                <input type="text" v-model="formData.address2" />
              </div>
            </div>

            <div class="field-row cols-2">
              <div class="field-group">
                <label>State <span class="req">*</span></label>
                <input type="text" v-model="formData.state" :class="errors['state'] ? 'is-error' : ''" />
                <span class="field-error" v-if="errors['state']">{{ errors['state'][0] }}</span>
              </div>
              <div class="field-group">
                <label>City <span class="req">*</span></label>
                <input type="text" v-model="formData.city_name" :class="errors['city_name'] ? 'is-error' : ''" />
                <span class="field-error" v-if="errors['city_name']">{{ errors['city_name'][0] }}</span>
              </div>
            </div>

            <div class="field-row cols-2">
              <div class="field-group">
                <label>Zip Code <span class="req">*</span></label>
                <input type="text" v-mask="'#####'" v-model="formData.zipcode" :class="errors['zipcode'] ? 'is-error' : ''" />
                <span class="field-error" v-if="errors['zipcode']">{{ errors['zipcode'][0] }}</span>
              </div>
              <div class="field-group">
                <label>Timezone <span class="req">*</span></label>
                <select v-model="formData.client_timezone" :class="errors['client_timezone'] ? 'is-error' : ''">
                  <option value="">Select timezone…</option>
                  <option v-for="(tz, i) in timeZones" :key="i" :value="tz">{{ tz }}</option>
                </select>
                <span class="field-error" v-if="errors['client_timezone']">{{ errors['client_timezone'][0] }}</span>
              </div>
            </div>

            <div class="field-row cols-2">
              <div class="field-group">
                <label>Contact Number</label>
                <input type="text" v-mask="'(###)-###-####'" v-model="formData.phone_number" :class="errors['phone_number'] ? 'is-error' : ''" />
                <span class="field-error" v-if="errors['phone_number']">{{ errors['phone_number'][0] }}</span>
              </div>
              <div class="field-group">
                <label>Payment Method</label>
                <select v-model="formData.payment_method" :class="errors['payment_method'] ? 'is-error' : ''">
                  <option value="">Select payment method…</option>
                  <option value="cardpointe">CardPointe</option>
                  <option value="heartland">Heartland</option>
                </select>
                <span class="field-error" v-if="errors['payment_method']">{{ errors['payment_method'][0] }}</span>
              </div>
            </div>
          </div>

          <!-- Settings -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-sliders"></i> Settings</div>

            <div class="toggle-row">
              <label class="switch-wrap">
                <input type="checkbox" v-model="is_active" />
                <span class="slider"></span>
              </label>
              <span class="toggle-label">Active</span>
            </div>

            <div class="toggle-row">
              <label class="switch-wrap">
                <input type="checkbox" v-model="formData.show_store_forward" id="switch-store" />
                <span class="slider"></span>
              </label>
              <span class="toggle-label">Show Store Forward</span>
            </div>

            <div class="toggle-row" style="margin-bottom:16px;">
              <label class="switch-wrap">
                <input type="checkbox" v-model="formData.review_enabled" id="feedback-switch" />
                <span class="slider"></span>
              </label>
              <span class="toggle-label">Allow Feedback Survey</span>
            </div>

            <div class="copy-from-row" style="margin-bottom:16px;">
              <label class="copy-checkbox">
                <input type="checkbox" v-model="formData.copy_from" />
                Copy services from
              </label>
              <div class="field-group" v-if="formData.copy_from" style="flex:1; min-width:180px;">
                <select v-model="formData.client_id">
                  <option value="null">Select provider…</option>
                  <option v-for="(client, i) in getserviceProviders" :key="i" :value="client.id">{{ client.name }}</option>
                </select>
                <span class="field-error" v-if="errors['client_id']">{{ errors['client_id'][0] }}</span>
              </div>
            </div>

            <div class="field-row cols-2">
              <div>
                <div style="margin-bottom:8px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.4px; color:#7a8499;">Provider Image</div>
                <div class="canvas-preview" v-if="showImage">
                  <canvas id="imageCanvas" ref="imageCanvas" style="width:200px; display:block;"></canvas>
                </div>
                <label class="upload-btn">
                  <input type="file" @change="updateCanvasImage($event, 1)" hidden />
                  <i class="fa fa-upload"></i> Upload Image
                </label>
                <span class="field-error" v-if="errors['image']">{{ errors['image'][0] }}</span>
              </div>
              <div>
                <div style="margin-bottom:8px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.4px; color:#7a8499;">Favicon / Icon</div>
                <div class="canvas-preview" v-if="showIcon">
                  <canvas id="iconcanvas" ref="iconcanvas" style="width:200px; display:block;"></canvas>
                </div>
                <label class="upload-btn">
                  <input type="file" @change="updateCanvasImage($event, 2)" hidden />
                  <i class="fa fa-upload"></i> Upload Icon
                </label>
                <span class="field-error" v-if="errors['fav_icon']">{{ errors['fav_icon'][0] }}</span>
              </div>
            </div>
          </div>

          <!-- Support Info -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-life-ring"></i> Support Info</div>
            <supporteditor
              v-model="formData.support_info"
              api-key="no-api-key"
              :init="{
                license_key: 'gpl',
                height: 200,
                menubar: false,
                plugins: ['advlist autolink lists link image charmap print preview anchor','searchreplace visualblocks code fullscreen','insertdatetime media table paste code help wordcount'],
                toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
              }"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js"
            />
          </div>

        </div>

        <!-- ── Right column ── -->
        <div class="right-col">

          <!-- Map -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-map"></i> Select Location</div>
            <span v-if="errors['long']" class="field-error" style="display:block; margin-bottom:8px;">Please select a location on the map</span>
            <keep-alive>
              <GoogleMap
                ref="map"
                @getFullAddress="setFullAddress"
                @dragPointer="setLocation"
                @onSearchClick="setLocation"
              />
            </keep-alive>
          </div>

          <!-- Google Business URL -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-google"></i> Google Business</div>
            <div class="field-group">
              <label>Google Business URL</label>
              <input type="text" v-model="formData.google_business_url" :class="errors['google_business_url'] ? 'is-error' : ''" placeholder="https://…" />
              <span class="field-error" v-if="errors['google_business_url']">{{ errors['google_business_url'][0] }}</span>
            </div>
          </div>

          <!-- User Information -->
          <div class="section-card">
            <div class="section-title"><i class="fa fa-user"></i> User Information</div>

            <div class="field-row cols-2">
              <div class="field-group">
                <label>First Name <span class="req">*</span></label>
                <input type="text" v-model="formData.first_name" :class="errors['first_name'] ? 'is-error' : ''" />
                <span class="field-error" v-if="errors['first_name']">{{ errors['first_name'][0] }}</span>
              </div>
              <div class="field-group">
                <label>Last Name <span class="req">*</span></label>
                <input type="text" v-model="formData.last_name" :class="errors['last_name'] ? 'is-error' : ''" />
                <span class="field-error" v-if="errors['last_name']">{{ errors['last_name'][0] }}</span>
              </div>
            </div>

            <div class="field-group" style="margin-bottom:14px;">
              <label>Password <span class="req">*</span></label>
              <input type="password" v-model="formData.password" :class="errors['password'] ? 'is-error' : ''" />
              <span class="field-error" v-if="errors['password']">{{ errors['password'][0] }}</span>
            </div>

            <div class="field-group" style="margin-bottom:14px;">
              <label>Email <span class="req">*</span></label>
              <input type="email" v-model="formData.email" :class="errors['email'] ? 'is-error' : ''" />
              <span class="field-error" v-if="errors['email']">{{ errors['email'][0] }}</span>
            </div>

            <div class="field-group">
              <label>Notification Email <span class="req">*</span></label>
              <input type="text" v-model="formData.notification_email" :class="errors['notification_email'] ? 'is-error' : ''" />
              <span class="field-error" v-if="errors['notification_email']">{{ errors['notification_email'][0] }}</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Footer -->
      <div class="form-footer">
        <button class="btn-cancel" @click="goBack()">
          <i class="fa fa-arrow-left"></i> Back
        </button>
        <div class="footer-right">
          <button
            v-if="!isEdit"
            class="btn-save secondary"
            ref="store_ssa"
            @click="store('SAA', 'store_ssa')"
          >
            Save &amp; Add Another
          </button>
          <button
            v-if="!isEdit"
            class="btn-save"
            ref="store_s"
            @click="store('S', 'store_s')"
          >
            <i class="fa fa-check"></i> Save
          </button>
          <button
            v-if="isEdit"
            class="btn-save"
            ref="kt_update_service"
            @click="update('kt_update_service')"
          >
            <i class="fa fa-check"></i> Update
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import GoogleMap from "@/components/GoogleMap.vue";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import Editor from "@tinymce/tinymce-vue";
import {
  CREATE_SERVICE_PROVIDER,
  GET_SERVICE_PROVIDER_LIST
} from "@/core/services/store/actions.type";
import { mapGetters } from "vuex";

export default {
  components: {
    GoogleMap,
    supporteditor: Editor
  },
  computed: {
    ...mapGetters(["getserviceProviders"])
  },
  data() {
    return {
      isEdit: false,
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "admin.dashboard" },
        { id: 2, title: "Service Provider", route: "admin.serviceprovider.index" },
        { id: 3, title: "Create Service Provider", route: "" }
      ],
      formData: {
        name: "",
        description: "",
        google_business_url: "",
        address: "",
        address2: "",
        state: "",
        city_name: "",
        zipcode: "",
        long: "",
        lat: "",
        client_timezone: "",
        first_name: "",
        last_name: "",
        email: "",
        username: "",
        slug: "",
        notification_email: "",
        payment_method: "",
        enable_on_site_payment: false,
        phone_number: "",
        confirm_password: "",
        password: "",
        client_id: "null",
        copy_from: false,
        fav_icon: "",
        show_store_forward: false,
        support_info: "",
        review_enabled: false
      },
      is_active: true,
      showImage: false,
      showIcon: false,
      image_base64: "",
      image_crop_base64: "",
      loading_data: true,
      loadingData: false,
      errors: [],
      timeZones: [],
      supporteditor: Editor,
      regEmail: /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/
    };
  },
  created() {
    this.timeZones = this.$moment.tz.names();
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.isEdit = this.$route.name === "admin.serviceprovider.edit";
    if (this.isEdit) this.breadCrumbs[2].title = "Edit | Service Provider";
    if (this.getserviceProviders.length === 0) this.getServiceProvider();
  },
  methods: {
    setFullAddress(data) {
      this.formData.address = data;
    },
    setLocation(location) {
      this.formData.long = location.lng;
      this.formData.lat = location.lat;
      this.formData.city_name = location.city.long_name;
      this.formData.address = location.formatted_address;
      this.formData.zipcode = location.postal_code.long_name;
      const state = location.state.short_name.toUpperCase();
      this.formData.state = state === undefined ? "" : state;
      this.formData.client_timezone = "";
    },
    goBack() {
      this.$router.push({ name: "admin.serviceprovider.index" });
    },
    getServiceProvider() {
      this.loadingData = true;
      this.$store.dispatch(GET_SERVICE_PROVIDER_LIST)
        .then(res => {
          if (res.code == 200) this.url = res.url;
          this.loadingData = false;
        })
        .catch(() => { this.loadingData = false; });
    },
    closeBtnLoad(ref) {
      const btn = this.$refs[ref];
      if (btn) {
        btn.classList.remove("spinner", "spinner-light", "spinner-right");
        btn.disabled = false;
      }
    },
    loadingButton(ref) {
      const btn = this.$refs[ref];
      if (btn) {
        btn.disabled = true;
        btn.classList.add("spinner", "spinner-light", "spinner-right");
      }
    },
    clearForm() {
      Object.assign(this.formData, {
        name: "", description: "", google_business_url: "", address: "", address2: "",
        state: "", city_name: "", zipcode: "", long: "", lat: "", client_timezone: "",
        payment_method: "", first_name: "", last_name: "", email: "", username: "",
        slug: "", notification_email: "", image: "", enable_on_site_payment: true,
        password: "", phone_number: "", client_id: "null", copy_from: false,
        fav_icon: "", support_info: "", show_store_forward: false, review_enabled: false
      });
      this.is_active = true;
      this.showImage = false;
      this.showIcon = false;
      this.image_base64 = "";
      this.errors = [];
    },
    store(status, ref) {
      this.formData.slug = this.formData.slug.replace(/\s/g, "").toLowerCase();
      this.formData.image = this.image_base64;
      this.formData.user_name = this.formData.first_name + " " + this.formData.last_name;
      this.formData.username = this.formData.email;
      this.formData.notification_email = this.formData.notification_email
        ? this.formData.notification_email.replace(" ", "")
        : "";
      this.$store.dispatch(CREATE_SERVICE_PROVIDER, { user_detail: this.formData, is_active: this.is_active })
        .then(data => {
          this.$toastr.s(data.msg);
          this.closeBtnLoad(ref);
          if (status === "SAA") {
            this.clearForm();
          } else {
            this.$router.push({ name: "admin.serviceprovider.index" });
          }
        })
        .catch(err => { this.errors = err; this.closeBtnLoad(ref); });
    },
    updateCanvasImage(e, val) {
      if (val === 1) this.showImage = true;
      else this.showIcon = true;
      const reader = new FileReader();
      reader.onload = e => {
        const img = new Image();
        img.onload = () => this.drawCanvasImage(img, val);
        img.src = e.target.result;
      };
      reader.readAsDataURL(e.target.files[0]);
    },
    drawCanvasImage(imageObj, val) {
      const canvas = val === 1 ? this.$refs.imageCanvas : this.$refs.iconcanvas;
      canvas.width = 240;
      canvas.height = (100 * imageObj.height) / imageObj.width;
      canvas.getContext("2d").drawImage(imageObj, 0, 0, 240, (100 * imageObj.height) / imageObj.width);
      const base64 = canvas.toDataURL("image/png");
      if (val === 1) this.image_base64 = base64;
      else this.formData.fav_icon = base64;
    }
  }
};
</script>

<style scoped>
input[type="number"] { -moz-appearance: textfield !important; appearance: textfield !important; }
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
</style>
